import React, { useState, useMemo, useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import Select, { components } from "react-select";
import Button from 'react-bootstrap/Button';
import { Stack, Toast, ToastContainer, Form, Modal } from "react-bootstrap";
import { TbGridDots } from 'react-icons/tb';
import { HiChevronDown, HiCheckCircle, HiXCircle } from 'react-icons/hi';
import { FaSpinner, FaTimes } from 'react-icons/fa';
import { BiExport, BiSearch, BiReset } from "react-icons/bi";
import { Tooltip } from '@mui/material';
import AdminMaster from "../../../Layout/admin/Master";
import { getretrivesyncmonitor, getSyncMonitorFiles } from "../../../Redux/Ffilemonitoring/action";
import { addDays, addMonths, endOfMonth, endOfWeek, endOfYear, startOfMonth, startOfWeek, startOfYear, subDays, setHours, setMinutes, setSeconds } from "date-fns";
import PartnerDropDown from "../../common/PartnerClientFacilityDropDown/PartnerDropDown";
import ClientDropDown from "../../common/PartnerClientFacilityDropDown/ClientDropDown";
import FilterComponent from "../../common/FilterComponent";
import TableHeadings from "../../common/TableHeadings";
import { downloadFile } from "../Dashboard/functions";
import useFilter from "../../../hooks/useFilter";
import DateRangePicker from 'rsuite/DateRangePicker';

const queueDatatableCustomStyles = {
    rows: {
        style: {
            minHeight: '20px', // override the row height
            fontSize: '0.7em',
            fontColor: '#000000',
            cursor: 'pointer',
            // width: '100%'

        },
    },

    headCells: {
        style: {
            paddingLeft: '5px', // override the cell padding for head cells
            paddingRight: '5px',
            textTransform: "uppercase",
            fontSize: '0.7em',
            fontWeight: '800',
            fontColor: '#000000 !important',
            opacity: 1,
            minHeight: "10px",
            // display : 'flex',
            // flexDirection: 'row',
            // justifyContent:'center'
        },
        draggingStyle: {
            cursor: 'move',
        },
    },
    cells: {
        style: {
            paddingLeft: '0px', // override the cell padding for data cells
            paddingRight: '0px',
            width: "100%",
            fontColor: '#000000',
            fontWeight: "550",
            cursor: 'pointer',
        },
    },
    pagination: {
        style: {
            color: '#000',
            fontSize: '0.8em',
            minHeight: '56px',
            backgroundColor: '#fff',
            borderTopStyle: 'solid',
            borderTopWidth: '1px',
            borderTopColor: '#f2f2f2',
        },
        pageButtonsStyle: {
            borderRadius: '50%',
            height: '40px',
            width: '40px',
            padding: '8px',
            margin: '0px',
            cursor: 'pointer',
            transition: '0.4s',
            color: '#000',
            fill: '#000',
            backgroundColor: 'transparent',
            '&:disabled': {
                cursor: 'unset',
                color: '#e2e2e2',
                fill: '#e2e2e2',
            },
            '&:hover:not(:disabled)': {
                backgroundColor: '#f2f2f2',
            },
            '&:focus': {
                outline: 'none',
                backgroundColor: '#f5f5f5',
            },
        },
    },
};

const predefinedBottomRanges = [
    {
        label: 'Today',
        value: [new Date().setHours(0, 0, 0, 0), new Date().setHours(23, 59, 59, 59)],
        placement: 'left'
    },
    {
        label: 'Yesterday',
        value: [addDays(new Date().setHours(0, 0, 0, 0), -1), addDays(new Date().setHours(23, 59, 59, 59), -1)],
        placement: 'left'       
    },
    {
        label: 'This week',
        value: [startOfWeek(new Date().setHours(0, 0, 0, 0)), new Date()],
        placement: 'left'
    },
    {
        label: 'Last 7 days',
        value: [subDays(new Date().setHours(0, 0, 0, 0), 6), new Date()],
        placement: 'left'
    },
    {
        label: 'Last 30 days',
        value: [subDays(new Date().setHours(0, 0, 0, 0), 29), new Date()],
        placement: 'left'
    },
    {
        label: 'This month',
        value: [startOfMonth(new Date().setHours(0, 0, 0, 0)), new Date()],
        placement: 'left'
    },
    {
        label: 'Last month',
        value: [startOfMonth(addMonths(new Date().setHours(0, 0, 0, 0), -1)), endOfMonth(addMonths(new Date(), -1))],
        placement: 'left'
    },
    {
        label: 'This year',
        value: [new Date(new Date().getFullYear(), 0, 1).setHours(0, 0, 0, 0), new Date()],
        placement: 'left'
    },
    {
        label: 'Last year',
        value: [setSeconds(setMinutes(setHours(startOfYear(new Date(new Date().getFullYear() - 1, 0, 1)), 0), 0), 0), setSeconds(setMinutes(setHours(endOfYear(new Date(new Date().getFullYear(), 0, 0)), 23), 59), 59)],
        placement: 'left'
    },

    {
        label: 'Last week',
        closeOverlay: false,
        value: value => {
            const [start = new Date().setHours(0, 0, 0, 0)] = value || [];
            return [
                addDays(startOfWeek(start, { weekStartsOn: 0 }), -7),
                addDays(endOfWeek(start, { weekStartsOn: 0 }), -7)
            ];
        },
        appearance: 'default'
    },
    {
        label: 'Next week',
        closeOverlay: false,
        value: value => {
            const [start = new Date()] = value || [];
            return [
                addDays(startOfWeek(start, { weekStartsOn: 0 }), 7),
                addDays(endOfWeek(start, { weekStartsOn: 0 }), 7)
            ];
        },
        appearance: 'default'
    }
];

const syncStatusOptions = [
    { value: 'all', label: 'All Sync' },
    { value: 'synced', label: 'Synced' },
    { value: 'not_synced', label: 'Not Synced' }
];

const SynchronizationMonitor = () => {
    const [loader] = useState({ id: null, type: null });
    const [colorRow, setColorRow] = useState({});
    const [toastMessage, setToastMessage] = useState({ status: false, message: "", type: "" });
    const [isSwal, setIsSwal] = useState({ status: false, message: "" });
    const [exportLoader, setExportLoader] = useState(false);
    const [monitordata, setMonitorData] = useState([]);
    const [resetCount, setResetCount] = useState(0);
    const [sortColumn, setSortColumn] = useState("");
    const [sortOrder, setSortOrder] = useState("");
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [modalConfig, setModalConfig] = useState({ rowId: null, apiKey: "" });
    const defaultColumns = {
        'S.NO': true,
        'Partner': true,
        'Client': true,
        'Received Start': true,
        'Received End': true,
        'Lockbox': true,
        'Revarc Lockbox': true,
        'Lockbox Files': true,
        'Revarc Lockbox File': true,
        '837 Lockbox': true,
        'Revarc 837 Lockbox': true,
        '837 Files': true,
        'Revarc 837 Files': true

    };

    const DropdownIndicator = (props) => {
        return (
            <components.DropdownIndicator {...props}>
                <HiChevronDown style={{ fontSize: "1.2em" }} />
            </components.DropdownIndicator>
        );
    };

    const [state, setState] = useState(defaultColumns);


    // const filterValidationSchema = Yup.object().shape({
    //     // Date_range: Yup.mixed().required('Date range is required')
    // });

    const initialFilters = {
        partner: [],
        client: [],
        mismatch_filter: true,
        range_start: null, 
        range_end: null,   
        zero_filter_type: []
    };

    const {
        searchFormik,
        data,
        loading,
        pageNo,
        limit,
        totalRows,
        handleSearch,
        handlePageChange,
        handlePerRowsChange,
        setSearchedData,
        searchedData,
        fetchData,
        setPageNo,
    } = useFilter(initialFilters, getretrivesyncmonitor);

    const initialModalFilters = {
        filename: "",
        status: "all",
        id: null,
        fieldname: ""



        
    };

    const {
        searchFormik: modalFormik,
        data: modalData,
        loading: modalLoading,
        pageNo: modalPageNo,
        limit: modalLimit,
        totalRows: modalTotalRows,
        handleSearch: handleModalSearch,
        handlePageChange: handleModalPageChange,
        handlePerRowsChange: handleModalPerRowsChange,
        setSearchedData: setModalSearchedData,
        fetchData: fetchModalData,
        setPageNo: setModalPageNo,
    } = useFilter(initialModalFilters, getSyncMonitorFiles);

    const handleCustomReset = () => {
        searchFormik.resetForm();
        handleSearch(initialFilters);
        setState(defaultColumns);
        setResetCount(prev => prev + 1);
        setColorRow({});
        setSortColumn("");
        setSortOrder("");
        setDateError(true);
    };

    const handleModalCustomReset = () => {
        const resetValues = { 
            ...initialModalFilters, 
            id: modalFormik.values.id, 
            fieldname: modalFormik.values.fieldname 
        };
        modalFormik.setValues(resetValues);
        handleModalSearch(resetValues);
    };

    const pageNoRef = useRef(pageNo);
    const limitRef = useRef(limit);

    useEffect(() => {
        pageNoRef.current = pageNo;
        limitRef.current = limit;
    }, [pageNo, limit]);


    const handleSort = (column, sortDirection) => {
        setSortColumn(column.id);
        setSortOrder(sortDirection);
        const newSearch = { ...searchedData, sort_key: column.sort_key, sort_order: sortDirection };
        setSearchedData(newSearch);
        setPageNo(1);
        fetchData(1, limit, newSearch);
    };

    const handleCountClick = (row, apiKey) => {
        setColorRow(row);
        const filters = {
            ...initialModalFilters,
            id: row?.id,
            fieldname: apiKey
        };
        modalFormik.setValues(filters);
        setModalSearchedData(filters);
        setShowDetailModal(true);
        fetchModalData(1, modalLimit, filters);
    };


    const columns = useMemo(
        () => [
            {
                id: 's_no',
                name: 'S.NO',
                selector: (row, index) => pageNoRef.current === 1 ? index + 1 : (pageNoRef.current - 1) * limitRef.current + index + 1,
                width: "60px",
                wrap: true,
                reorder: true,
                omit: state['S.NO'] === false,
            },
            {
                id: 'partner_name',
                name: 'Partner',
                sort_key: 'partner_name',
                selector: row => row?.partner_name,
                cell: (row) => (
                    <div onClick={()=>handleRowCheckClick(row)} className="overflow-hidden d-flex flex-row flex-wrap text-truncate" style={{ width: "100%", height: "100%", padding: '0 5px' }}>
                        <Tooltip disableInteractive title={row?.partner_name} placement='top' arrow>
                            <span className='text-truncate'>{row?.partner_name}</span>
                        </Tooltip>
                    </div>
                ),
                sortable: true,
                left: true,
                wrap: true,
                width: '160px',
                reorder: true,
                omit: state['Partner'] === false,
            },
            {
                id: 'client_name',
                name: 'Client',
                sort_key: 'client_name',
                selector: row => row?.client_name,
                cell: (row) => (
                    <div onClick={()=>handleRowCheckClick(row)} className="overflow-hidden d-flex flex-row flex-wrap text-truncate" style={{ width: "100%", height: "100%", padding: '0 5px' }}>
                        <Tooltip disableInteractive title={row?.client_name} placement='top' arrow>
                            <span className='text-truncate'>{row?.client_name}</span>
                        </Tooltip>
                    </div>
                ),
                sortable: true,
                left: true,
                wrap: true,
                width: '160px',
                reorder: true,
                omit: state['Client'] === false,
            },
            {
                id: "range_start",
                name: "Received Start (XTRACT)",
                sort_key: "range_start",
                selector: row => row?.range_start,
                cell: (row) => (
                    <div onClick={()=>handleRowCheckClick(row)} className="overflow-hidden d-flex flex-row flex-wrap text-truncate" style={{ width: "100%", height: "100%", padding: '0 5px' }}>
                        <Tooltip disableInteractive title={row?.range_start} placement='top' arrow>
                            <span className='text-truncate'>{row?.range_start ? row?.range_start : ""}</span>
                        </Tooltip>
                    </div>
                ),
                sortable: true,
                left: true,
                wrap: true,
                width: '120px',
                reorder: true,
                omit: state['Received Start'] === false,
            },
            {
                id: "range_end",
                name: "Received End (XTRACT)",
                sort_key: "range_end",
                selector: row => row?.range_end,
                cell: (row) => (
                    <div onClick={()=>handleRowCheckClick(row)} className="overflow-hidden d-flex flex-row flex-wrap text-truncate" style={{ width: "100%", height: "100%", padding: '0 5px' }}>
                        <Tooltip disableInteractive title={row?.range_end} placement='top' arrow>
                            <span className='text-truncate'>{row?.range_end ? row?.range_end : ""}</span>
                        </Tooltip>
                    </div>
                ),
                sortable: true,
                left: true,
                wrap: true,
                width: '120px',
                reorder: true,
                omit: state['Received End'] === false,
            },
              {
                id: "eob_lockbox_count",
                api_key: "eob_lockbox",
                name: "Lockbox",
                sort_key: "eob_lockbox_count",
                selector: row => row?.eob_lockbox_count,
                cell: (row) => (
                    <div 
                        onClick={() => handleRowCheckClick(row)}
                        className="overflow-hidden d-flex flex-row flex-wrap text-truncate justify-content-end align-items-center" 
                        style={{ 
                            width: "100%", 
                            height: '100%', 
                            padding: '0 10px',
                            backgroundColor: (row?.eob_lockbox_count !== row?.eob_lockbox_rc_count && !searchedData?.mismatch_filter) ? '#ffe7e7' : 'transparent'
                        }}
                    >
                        <Tooltip disableInteractive title={row?.eob_lockbox_count} placement='top' arrow>
                            <span 
                                className='text-truncate' 
                                style={{ 
                                    cursor: 'pointer',
                                    textDecoration: 'underline'
                                }}
                                onClick={() => handleCountClick(row, 'eob_lockbox_count')}
                            >
                                {row?.eob_lockbox_count}
                            </span>
                        </Tooltip>
                    </div>
                ),
                sortable: true,
                reorder: true,
                right: true,
                width: '80px',
                omit: state['Lockbox'] === false,
            },
            {
                id: "eob_lockbox_rc_count",
                api_key: "eob_lockbox_rc",
                name: "Revarc Lockbox",
                sort_key: "eob_lockbox_rc_count",
                selector: row => row?.eob_lockbox_rc_count,
                cell: (row) => (
                    <div 
                        onClick={() => handleRowCheckClick(row)}
                        className="overflow-hidden d-flex flex-row flex-wrap text-truncate justify-content-end align-items-center" 
                        style={{ 
                            width: "100%", 
                            height: '100%', 
                            padding: '0 10px',
                            backgroundColor: (row?.eob_lockbox_count !== row?.eob_lockbox_rc_count && !searchedData?.mismatch_filter) ? '#ffe7e7' : 'transparent'
                        }}
                    >
                        <Tooltip disableInteractive title={row?.eob_lockbox_rc_count} placement='top' arrow>
                            <span 
                                className='text-truncate' 
                                style={{ 
                                    cursor: 'pointer',
                                    textDecoration: 'underline'
                                }}
                                onClick={() => handleCountClick(row, 'eob_lockbox_rc_count')}
                            >
                                {row?.eob_lockbox_rc_count}
                            </span>
                        </Tooltip>
                    </div>
                ),
                sortable: true,
                reorder: true,
                right: true,
                width: '100px',
                omit: state['Revarc Lockbox'] === false,
            },
            {
                id: "eob_file_count",
                api_key: "eob_file",
                name: "Lockbox Files",
                sort_key: "eob_file_count",
                selector: row => row?.eob_file_count,
                cell: (row) => (
                    <div 
                        onClick={() => handleRowCheckClick(row)}
                        className="overflow-hidden d-flex flex-row flex-wrap text-truncate justify-content-end align-items-center" 
                        style={{ 
                            width: "100%", 
                            height: '100%', 
                            padding: '0 10px',
                            backgroundColor: (row?.eob_file_count !== row?.eob_file_rc_count && !searchedData?.mismatch_filter) ? '#ffe7e7' : 'transparent'
                        }}
                    >
                        <Tooltip disableInteractive title={row?.eob_file_count} placement='top' arrow>
                            <span 
                                className='text-truncate' 
                                style={{ 
                                    cursor: 'pointer',
                                    textDecoration: 'underline'
                                }}
                                onClick={() => handleCountClick(row, 'eob_file_count')}
                            >
                                {row?.eob_file_count}
                            </span>
                        </Tooltip>
                    </div>
                ),
                sortable: true,
                reorder: true,
                right: true,
                width: '100px',
                omit: state['Lockbox Files'] === false,
            },
            {
                id: "eob_file_rc_count",
                api_key: "eob_file_rc",
                name: "Revarc Lockbox File",
                sort_key: "eob_file_rc_count",
                selector: row => row?.eob_file_rc_count,
                cell: (row) => (
                    <div 
                        onClick={() => handleRowCheckClick(row)}
                        className="overflow-hidden d-flex flex-row flex-wrap text-truncate justify-content-end align-items-center" 
                        style={{ 
                            width: "100%", 
                            height: '100%', 
                            padding: '0 10px',
                            backgroundColor: (row?.eob_file_count !== row?.eob_file_rc_count && !searchedData?.mismatch_filter) ? '#ffe7e7' : 'transparent'
                        }}
                    >
                        <Tooltip disableInteractive title={row?.eob_file_rc_count} placement='top' arrow>
                            <span 
                                className='text-truncate' 
                                style={{ 
                                    cursor: 'pointer',
                                    textDecoration: 'underline'
                                }}
                                onClick={() => handleCountClick(row, 'eob_file_rc_count')}
                            >
                                {row?.eob_file_rc_count}
                            </span>
                        </Tooltip>
                    </div>
                ),
                sortable: true,
                reorder: true,
                right: true,
                width: '100px',
                omit: state['Revarc Lockbox File'] === false,
            },
            {
                id: "_837_lockbox_count",
                api_key: "837_lockbox",
                name: "837 Lockbox",
                sort_key: "_837_lockbox_count",
                selector: row => row?._837_lockbox_count,
                cell: (row) => (
                    <div 
                        onClick={() => handleRowCheckClick(row)}
                        className="overflow-hidden d-flex flex-row flex-wrap text-truncate justify-content-end align-items-center" 
                        style={{ 
                            width: "100%", 
                            height: '100%', 
                            padding: '0 10px',
                            backgroundColor: (row?._837_lockbox_count !== row?._837_lockbox_rc_count && !searchedData?.mismatch_filter) ? '#ffe7e7' : 'transparent'
                        }}
                    >
                        <Tooltip disableInteractive title={row?._837_lockbox_count} placement='top' arrow>
                            <span 
                                className='text-truncate' 
                                style={{ 
                                    cursor: 'pointer',
                                    textDecoration: 'underline'
                                }}
                                onClick={() => handleCountClick(row, '_837_lockbox_count')}
                            >
                                {row?._837_lockbox_count}
                            </span>
                        </Tooltip>
                    </div>
                ),
                sortable: true,
                reorder: true,
                right: true,
                width: '100px',
                omit: state['837 Lockbox'] === false,
            },
            {
                id: "_837_lockbox_rc_count",
                api_key: "837_lockbox_rc",
                name: "Revarc 837 Lockbox",
                sort_key: "_837_lockbox_rc_count",
                selector: row => row?._837_lockbox_rc_count,
                cell: (row) => (
                    <div 
                        onClick={() => handleRowCheckClick(row)}
                        className="overflow-hidden d-flex flex-row flex-wrap text-truncate justify-content-end align-items-center" 
                        style={{ 
                            width: "100%", 
                            height: '100%', 
                            padding: '0 10px',
                            backgroundColor: (row?._837_lockbox_count !== row?._837_lockbox_rc_count && !searchedData?.mismatch_filter) ? '#ffe7e7' : 'transparent'
                        }}
                    >
                        <Tooltip disableInteractive title={row?._837_lockbox_rc_count} placement='top' arrow>
                            <span 
                                className='text-truncate' 
                                style={{ 
                                    cursor: 'pointer',
                                    textDecoration: 'underline'
                                }}
                                onClick={() => handleCountClick(row, '_837_lockbox_rc_count')}
                            >
                                {row?._837_lockbox_rc_count}
                            </span>
                        </Tooltip>
                    </div>
                ),
                sortable: true,
                reorder: true,
                right: true,
                width: '100px',
                omit: state['Revarc 837 Lockbox'] === false,
            },
            {
                id: "_837_file_count",
                api_key: "837_file",
                name: "837 Files ",
                sort_key: "_837_file_count",
                selector: row => row?._837_file_count,
                cell: (row) => (
                    <div 
                        onClick={() => handleRowCheckClick(row)}
                        className="overflow-hidden d-flex flex-row flex-wrap text-truncate justify-content-end align-items-center" 
                        style={{ 
                            width: "100%", 
                            height: '100%', 
                            padding: '0 10px',
                            backgroundColor: (row?._837_file_count !== row?._837_file_rc_count && !searchedData?.mismatch_filter) ? '#ffe7e7' : 'transparent'
                        }}
                    >
                        <Tooltip disableInteractive title={row?._837_file_count} placement='top' arrow>
                            <span 
                                className='text-truncate' 
                                style={{ 
                                    cursor: 'pointer',
                                    textDecoration: 'underline'
                                }}
                                onClick={() => handleCountClick(row, '_837_file_count')}
                            >
                                {row?._837_file_count}
                            </span>
                        </Tooltip>
                    </div>
                ),
                sortable: true,
                reorder: true,
                right: true,
                width: '100px',
                omit: state['837 Files'] === false,
            },
            {
                id: "_837_file_rc_count",
                api_key: "837_file_rc",
                name: "Revarc 837 Files",
                sort_key: "_837_file_rc_count",
                selector: row => row?._837_file_rc_count,
                cell: (row) => (
                    <div 
                        onClick={() => handleRowCheckClick(row)}
                        className="overflow-hidden d-flex flex-row flex-wrap text-truncate justify-content-end align-items-center" 
                        style={{ 
                            width: "100%", 
                            height: '100%', 
                            padding: '0 10px',
                            backgroundColor: (row?._837_file_count !== row?._837_file_rc_count && !searchedData?.mismatch_filter) ? '#ffe7e7' : 'transparent'
                        }}
                    >
                        <Tooltip disableInteractive title={row?._837_file_rc_count} placement='top' arrow>
                            <span 
                                className='text-truncate' 
                                style={{ 
                                    cursor: 'pointer',
                                    textDecoration: 'underline'
                                }}
                                onClick={() => handleCountClick(row, '_837_file_rc_count')}
                            >
                                {row?._837_file_rc_count}
                            </span>
                        </Tooltip>
                    </div>
                ),
                sortable: true,
                reorder: true,
                right: true,
                width: '100px',
                omit: state['Revarc 837 Files'] === false,
            },
            
          
        ],
        [state ,searchedData]
    );

    const modalColumns = useMemo(
        () => [
            {
                name: 'S.NO',
                selector: (row, index) => (modalPageNo - 1) * modalLimit + index + 1,
                width: "70px",
            },
            {
                name: 'File Name',
                selector: row => row?.file_name,
                sortable: true,
            },
            {
                name: 'Status',
                selector: row => row?.is_synced,
                sortable: true,
                cell: (row) => (
                    <div className="d-flex align-items-center">
                        {row?.is_synced ? (
                            <Tooltip title="Synced" placement="top" arrow>
                                <HiCheckCircle className="text-success ms-2" style={{ fontSize: '1.5em' }} />
                            </Tooltip>
                        ) : (
                            <Tooltip title="Not Synced" placement="top" arrow>
                                <HiXCircle className="text-danger ms-2" style={{ fontSize: '1.5em' }} />
                            </Tooltip>
                        )}
                    </div>
                )
            }
        ],
        [modalPageNo, modalLimit]
    );
    
    const conditionalRowStyles = useMemo(() => [
        {
            when: (row) => colorRow?.id === row?.id,
            style:(row) => ({
                backgroundColor: colorRow?.id == row.id ? '#dbf4f5' : 'inherit',
                cursor: "pointer",
            }),
        },
    ], [colorRow]);

    const handleRowCheckClick = (row) => {
        setColorRow(row);
    };

    
    const [dateError, setDateError] = useState(true);

    const handleOnDateTimeChange = (value) => {
        searchFormik.setFieldValue("range_start", value);
        if (value) setDateError(false);
    };

    const handleOnDateTimeChangeEnd = (value) => {
        searchFormik.setFieldValue("range_end", value);
        if (value) setDateError(false);
    };

    const handleSearchWithValidation = (values) => {
        if (!values.range_start && !values.range_end) {
            setDateError(true);
            return;
        }
        setDateError(false);
        handleSearch(values);
    };

    const handleExport = () => {
        setExportLoader(true);
        let header_names = [];
        const headerMap = {
            'Partner': "partner_name",
            'Client': "client_name",
            'Received Start': "range_start",
            'Received End': "range_end",
            'Lockbox': "eob_lockbox_count",
            'Revarc Lockbox': "eob_lockbox_rc_count",
            'Lockbox Files': "eob_file_count",
            'Revarc Lockbox File': "eob_file_rc_count",
            '837 Lockbox': "_837_lockbox_count",
            'Revarc 837 Lockbox': "_837_lockbox_rc_count",
            '837 Files': "_837_file_count",
            'Revarc 837 Files': "_837_file_rc_count",
        };

        for (let key in headerMap) {
            if (state[key]) {
                header_names.push(headerMap[key]);
            }
        }
        getretrivesyncmonitor(pageNo, limit, searchedData, true, header_names.join(','))((response) => {
            if (response?.status && response?.data) {
                downloadFile(response?.data, null, true);
            }
            setExportLoader(false);
        });
    };




    return (
        <React.Fragment>
            <AdminMaster ToastMessage={toastMessage} />

            <ToastContainer className="p-3" position={"top-end"}>
                <Toast
                    bg={toastMessage?.type}
                    onClose={() => setToastMessage({ status: false, message: "", type: "" })}
                    show={toastMessage?.status}
                    animation={true}
                    delay={2000}
                    autohide
                >
                    <Toast.Body><span className='text-white'>{toastMessage?.message}</span></Toast.Body>
                </Toast>
            </ToastContainer>

            {isSwal.status && (
                <SweetAlert
                    danger
                    confirmBtnText="Ok!"
                    confirmBtnCssClass="custom-confirm-button-class"
                    onConfirm={() => setIsSwal({ status: false, message: "" })}
                >
                    <span className="fw-bold">{isSwal.message}</span>
                </SweetAlert>
            )}

            <main id="main" className="main">
                <div className="pagetitle tab-fixed-header d-flex align-items-center">
                    <h1 id='page-title'>Synchronization Monitor </h1>
                    {data?.last_sync_time?.sync_finish_timestamp && (
                        <span className="pt-2 ms-3" style={{color:'#1ba2a8'}}>Last Sync : {data?.last_sync_time?.sync_finish_timestamp}</span>
                    )}
                </div>

                <section className="section mt-5">
                    <div className="row align-items-top gap-2 gap-md-5">
                        <div className="col-lg-12">
                            <div className="card mb-5 mb-xl-8">
                                <div className='card-header'>
                                    <div className="d-flex align-items-center position-relative ms-4">
                                        <FilterComponent
                                            formik={searchFormik}
                                            onReset={handleCustomReset}
                                            onSearch={handleSearchWithValidation}
                                            extraAction={(
                                                <div className="d-flex">
                                                    <div className="me-2">
                                                        <TableHeadings
                                                            columnState={state}
                                                            setColumnState={setState}
                                                        />
                                                    </div>
                                                    <div>
                                                        <Tooltip disableInteractive title="Export" placement='top' arrow>
                                                            <Button
                                                                className="btn btn-light btn-active-primary"
                                                                onClick={handleExport}
                                                                disabled={exportLoader}
                                                            >
                                                                {exportLoader ? (
                                                                    <div class="spinner-border spinner-border-sm" role="status">
                                                                        <span class="visually-hidden">Loading...</span>
                                                                    </div>
                                                                ) : (
                                                                    <BiExport className="svg-icon svg-icon-3" />
                                                                )}
                                                            </Button>
                                                        </Tooltip>
                                                    </div>
                                                </div>
                                            )}
                                        >
                                            <PartnerDropDown lockboxformik={searchFormik} name="partner" />
                                            <ClientDropDown lockboxformik={searchFormik} name="client" />
                                            <div className='' >
                                                <Tooltip title="Received Start (XTRACT)" placement="top" disableInteractive>

                                                    <Stack direction="column" spacing={8} alignItems="flex-start">
                                                        <DateRangePicker
                                                            ranges={predefinedBottomRanges}
                                                            cleanable={true}
                                                            placement='auto'
                                                            defaultCalendarValue={[new Date().setHours(0, 0, 0, 0), new Date().setHours(23, 59, 59, 59)]}
                                                            appearance="default"
                                                            format={"MM/dd/yyyy HH:mm"}
                                                            placeholder={"MM/DD/YYYY - MM/DD/YYYY"}
                                                            className={`form-date-control form-control-solid b-r-1 text-dark jobmonitor-datepicker ${
                                                                dateError ? 'error-border' : ''
                                                            }`}
                                                            name="range_start"
                                                            onChange={(value) => { handleOnDateTimeChange(value) }}
                                                            value={searchFormik?.values?.range_start}
                                                        />
                                                    </Stack>
                                                </Tooltip>

                                            </div>


                                            
                                            <div className='' >
                                                <Tooltip title="Received End (XTRACT)" placement="top" disableInteractive>

                                                    <Stack direction="column" spacing={8} alignItems="flex-start">
                                                        <DateRangePicker
                                                            ranges={predefinedBottomRanges}
                                                            cleanable={true}
                                                            placement='auto'
                                                            defaultCalendarValue={[new Date().setHours(0, 0, 0, 0), new Date().setHours(23, 59, 59, 59)]}
                                                            appearance="default"
                                                            format={"MM/dd/yyyy HH:mm"}
                                                            placeholder={"MM/DD/YYYY - MM/DD/YYYY"}
                                                            className={`form-date-control form-control-solid b-r-1 text-dark jobmonitor-datepicker ${
                                                                dateError ? 'error-border' : ''
                                                            }`}

                                                            name="range_end"
                                                            onChange={(value) => { handleOnDateTimeChangeEnd(value) }}
                                                            value={searchFormik?.values?.range_end}
                                                        />
                                                    </Stack>
                                                </Tooltip>

                                            </div>

                                            <div className="d-flex align-items-center ms-2" style={{ minHeight: '40px' }}>
                                                <Tooltip title={searchFormik.values?.mismatch_filter ? 'All' : 'Mismatch'}placement="top" arrow>
                                                    <div>
                                                        <Form.Check 
                                                            type="switch"
                                                            id="mismatch-filter-toggle"
                                                            checked={searchFormik.values.mismatch_filter}
                                                            onChange={(e) => searchFormik.setFieldValue("mismatch_filter", e.target.checked)}
                                                            style={{ fontSize: '16px', fontWeight: '500' }}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            </div>


                                        </FilterComponent>
                                    </div>
                                </div>

                                <div className="card-body" style={{ padding: "0px 2.25rem" }}>
                                    <div className="table-responsive">
                                        <div className="dashboardtable">
                                            <DataTable
                                                key={resetCount}
                                                className="custom-datatable"
                                                fixedHeader
                                                fixedHeaderScrollHeight="calc(100vh - 280px)"
                                                data={data?.results || []}
                                                columns={columns}
                                                striped
                                                progressPending={loading}
                                                progressComponent={<div className="p-5">Loading...</div>}
                                                noDataComponent={<div className="p-5 text-muted">No synchronization records found</div>}
                                                pagination
                                                paginationServer
                                                paginationTotalRows={totalRows}
                                                onSort={handleSort}
                                                sortServer
                                                sortColumn={sortColumn}
                                                sortDirection={sortOrder}
                                                paginationPerPage={limit}
                                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                                onChangeRowsPerPage={handlePerRowsChange}
                                                onChangePage={handlePageChange}
                                                conditionalRowStyles={conditionalRowStyles}
                                                onRowClicked={handleRowCheckClick}
                                                theme="solarized"
                                                customStyles={queueDatatableCustomStyles}
                                                pointerOnHover
                                                
                                                highlightOnHover
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
                    <Modal.Header closeButton>
                        <Modal.Title className="table-report-modelTitle">Synchronization Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="mb-4">
                            <FilterComponent
                                formik={modalFormik}
                                onReset={handleModalCustomReset}
                                className="p-0 bg-transparent border-0"
                            >
                                <div className="position-relative" style={{ width: '250px' }}>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search Filename"
                                        name="filename"
                                        value={modalFormik.values.filename}
                                        onChange={modalFormik.handleChange}
                                        className="form-control-solid"
                                        style={{ paddingRight: '30px', borderRadius: '8px' }}
                                    />
                                    {modalFormik.values.filename && (
                                        <FaTimes 
                                            className="position-absolute text-muted" 
                                            style={{ right: '10px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }} 
                                            onClick={() => modalFormik.setFieldValue("filename", "")}
                                        />
                                    )}
                                </div>
                                <div className="d-flex align-items-center ms-2" style={{ minHeight: '40px', gap: '8px' }}>
                                    <Tooltip title={modalFormik.values.status === 'all' ? 'All' : 'Not Synced'} placement="top" arrow>
                                        <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                                           
                                            <Form.Check
                                                type="switch"
                                                id="modal-status-toggle"
                                                checked={modalFormik.values.status === 'all'}
                                                onChange={(e) => modalFormik.setFieldValue("status", e.target.checked ? 'all' : false)}
                                                style={{ fontSize: '16px' }}
                                            />
                                        </div>
                                    </Tooltip>
                                </div>
                            </FilterComponent>
                        </div>

                        <div className="dashboardtable">
                            <DataTable
                                columns={modalColumns}
                                data={modalData?.results || []}
                                pagination
                                paginationServer
                                navigation
                                striped
                                paginationTotalRows={modalTotalRows}
                                onChangeRowsPerPage={handleModalPerRowsChange}
                                onChangePage={handleModalPageChange}
                                progressPending={modalLoading}
                                progressComponent={<div className="p-4 text-center"><FaSpinner className="spinner" /> Loading...</div>}
                                noDataComponent={<div className="p-4 text-muted text-center">No file records found</div>}
                                fixedHeader
                                fixedHeaderScrollHeight="400px"
                                customStyles={queueDatatableCustomStyles}
                                highlightOnHover
                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                pointerOnHover
                                paginationPerPage={modalLimit}
                            />
                        </div>
                    </Modal.Body>
                </Modal>
            </main>
        </React.Fragment>
    );
};

export default SynchronizationMonitor;